import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UNKNOWN_LABEL } from '@core/constants';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { Parser } from 'xml2js';
import { pinturaLabels } from '@core/services/library/pintura-labels.model';
import {
    getEditorDefaults,
    createMarkupEditorToolbar
} from '@pqina/pintura';

@Injectable({
    providedIn: 'root'
})
export class LibraryService {
    // Idiomas con fichero real en assets/translations (servido como assets/i18n):
    // cualquier otro código provoca un 404 al cargar/recargar labels
    private static readonly SHIPPED_LOCALES = ['ca', 'en', 'es', 'fr', 'it', 'mx', 'pt', 'ro', 'ru'];

    private pinturaLabels: any;

    constructor(private translateService: TranslateService) {
    }

    public get locale(): string {
        return this.translateService.currentLang;
    }

    public get pintura() {
        return this.pinturaLabels;
    }

    getLanguageCode(): string {
        const language = navigator.language.toLowerCase();
        const languageCode = language.substr(0, 2);
        const countryCode = language.substr(-2);

        // Variantes de español sin fichero propio (es-419 → '19', es-AR → 'ar'...)
        // deben caer a 'es' para no pedir un JSON inexistente
        if (languageCode === 'es') {
            return this.toShippedLocale(countryCode, 'es');
        }

        return this.toShippedLocale(languageCode, 'en');
    }

    toShippedLocale(locale: string, fallback: string): string {
        return LibraryService.SHIPPED_LOCALES.includes(locale) ? locale : fallback;
    }

    getLabel(key: string): string {
        let label: string;

        label = this.translateService.instant(key);

        if (label === key) {
            label = UNKNOWN_LABEL.replace('###', key);
        }

        return label;
    }

    getLabelAsync(key: string): Observable<string> {
        return this.translateService.getStreamOnTranslationChange(key)
            .pipe(map(label => label === key ? UNKNOWN_LABEL.replace('###', key) : label));
    }

    loadLabels(locale: string, xmlString: string): void {
        const { labels } = this.xmlToJson(xmlString);
        const labelsArray = { };

        labels.label.forEach(({ id, _ }) => {
            labelsArray[id] = _?.replace(/\\n/g, '\n');
        });

        this.translateService.setTranslation(locale, labelsArray, true);

        const editorDefaults = getEditorDefaults();
        const filteredDefaults = {};

        if (editorDefaults && typeof editorDefaults === 'object') {
            Object.keys(editorDefaults).forEach(key => {
                const value = editorDefaults[key];
                // Solo incluir strings y funciones
                if (typeof value === 'string' || typeof value === 'function') {
                    filteredDefaults[key] = value;
                }
            });
        }

        this.pinturaLabels = {
            ...filteredDefaults,
            ...this.buildPinturaLabels(),
        };
    }

    removeOnlineTranslations(): Promise<void> {
        return new Promise(resolve => {
            const locale = this.toShippedLocale(this.locale, 'en');
            // El GET de i18n pasa por los interceptores HTTP y puede fallar o ser
            // silenciado durante un logout: la promesa debe resolver SIEMPRE
            // (finalize + timeout de seguridad) o el logout quedaría bloqueado
            const safety = setTimeout(resolve, 5000);
            this.translateService.reloadLang(locale)
                .pipe(finalize(() => {
                    clearTimeout(safety);
                    resolve();
                }))
                .subscribe({
                    next: () => this.translateService.setTranslation(locale, { }, true),
                    error: () => { }
                });
        });
    }

    private buildPinturaLabels() {
        let translatedLabels = { ...pinturaLabels };

        Object.entries(pinturaLabels)
            .filter(([ _, value ]) => typeof value === 'string')
            .forEach(([ key ]) => {
                translatedLabels[key] = this.getLabel(`pintura_${key}`);
            });

        const labelSupportError = (features) => `${this.getLabel('pintura_labelSupportError')} ${features.join(', ')}`;
        const statusLabelLoadImage = (state) => {
            if (!state || !state.task) {
                return this.getLabel('pintura_statusLabelLoadImage_wait');
            }
            if (state.error) {
                return state.error.code === 'IMAGE_TOO_SMALL'
                    ? `${this.getLabel('pintura_statusLabelLoadImage_size')} {minWidth} &times; {minHeight}`
                    : this.getLabel('pintura_statusLabelLoadImage_error');
            }
            if (state.task === 'blob-to-bitmap') {
                return `${this.getLabel('pintura_statusLabelLoadImage_prepare')}&hellip;`;
            }
            return `${this.getLabel('pintura_statusLabelLoadImage_load')}&hellip;`;

        };
        const statusLabelProcessImage = (state) => {
            if (!state || !state.task) {
                return undefined;
            }
            if (state.task === 'store') {
                if (state.error) {
                    return this.getLabel('pintura_statusLabelProcessImage_upload_error');
                }
                return `${this.getLabel('pintura_statusLabelProcessImage_upload')}&hellip;`;
            }
            if (state.error) {
                return this.getLabel('pintura_statusLabelProcessImage_process_error');
            }
            return `${this.getLabel('pintura_statusLabelProcessImage_process')}&hellip;`;
        };

        translatedLabels = {
            ...translatedLabels,
            labelSupportError,
            statusLabelLoadImage,
            statusLabelProcessImage,
        };

        return translatedLabels;
    }

    private xmlToJson(xml: string): any {
        const xmlParser = new Parser({ explicitArray: false, ignoreAttrs: false, mergeAttrs: true });
        let json = { };

        xmlParser.parseString(xml, (err, result) => {
            json = result;
        });

        return json;
    }
}
